import { useState } from 'react'
import { OverlayPortal } from './ModalPortal'
import { X, Send, Check, Mail } from 'lucide-react'
import { casesAPI } from '../services/api'
import { useI18n } from '../i18n'
import { OrellaSpinner } from './OrellaLoader' // s123: the app's one spinner

/**
 * RemindDoctorSheet — v229.5.0
 * External/outside-MD conversion hook. The patient enters their doctor's email
 * and Orella sends a house-styled invite to claim the case (which unlocks Deliver
 * via the existing External→Orella conversion). Fax is a planned follow-on and is
 * shown disabled ("coming soon").
 */
export default function RemindDoctorSheet({ caseId, onClose }) {
  const { t } = useI18n()
  const [email, setEmail] = useState('')
  const [status, setStatus] = useState('input') // input | sending | sent | error
  const [error, setError] = useState('')

  const NAVY = '#15354F', BLUE = '#46A8F9', LBLUE = '#A7D9FC', ICE = '#E9F8FF', SLATE = '#5F7A8E'
  const valid = /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim())

  const send = async () => {
    if (!valid || status === 'sending') return
    setStatus('sending'); setError('')
    try {
      const res = await casesAPI.remindDoctor(caseId, email.trim())
      if (res?.success) {
        setStatus('sent')
      } else {
        setStatus('error')
        setError(res?.error || t('remindDoctor.errorGeneric', 'Could not send the email right now. Please try again.'))
      }
    } catch (e) {
      setStatus('error')
      setError(e?.error || e?.message || t('remindDoctor.errorGeneric', 'Could not send the email right now. Please try again.'))
    }
  }

  return (
    <OverlayPortal>
    <div
      className="fixed inset-0 z-[60] flex items-end justify-center"
      style={{ background: 'rgba(21,53,79,0.45)' }}
      onClick={onClose}
    >
      <div
        className="w-full max-w-[480px] rounded-t-[22px] bg-white p-5 pb-7 bottom-actions [--ba-min:28px]"
        style={{ animation: 'caseHomeStagger 0.22s ease both' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mx-auto mb-4 h-1 w-10 rounded-full" style={{ background: '#d7dee6' }} />

        {status === 'sent' ? (
          <div className="text-center px-1">
            <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-full" style={{ background: '#E9FAEC' }}>
              <Check size={26} style={{ color: '#16A34A' }} strokeWidth={2.6} />
            </div>
            <h3 className="text-[18px] font-extrabold" style={{ color: NAVY }}>
              {t('remindDoctor.sentTitle', 'Reminder sent')}
            </h3>
            <p className="mt-2 text-[13px] leading-relaxed" style={{ color: '#64748B' }}>
              {t('remindDoctor.sentBody', "We emailed your doctor an invite to claim this case on Orella. If they join, your Deliver step unlocks automatically.")}
            </p>
            <button
              onClick={onClose}
              className="mt-5 w-full rounded-xl py-3 text-[14.5px] font-bold text-white"
              style={{ background: BLUE, boxShadow: '0 4px 12px rgba(70,168,249,0.3)' }}
            >
              {t('common.done', 'Done')}
            </button>
          </div>
        ) : (
          <>
            <div className="flex items-start justify-between">
              <h3 className="text-[17px] font-extrabold" style={{ color: NAVY }}>
                {t('remindDoctor.title', 'Remind your doctor')}
              </h3>
              <button onClick={onClose} className="-mr-1 -mt-1 p-1" aria-label="Close">
                <X size={20} style={{ color: SLATE }} />
              </button>
            </div>
            <p className="mt-1 mb-4 text-[12.7px] leading-relaxed" style={{ color: '#64748B' }}>
              {t('remindDoctor.subtitle', 'Send a quick note inviting your doctor to claim this case on Orella and unlock your treatment & follow-up.')}
            </p>

            {/* Email */}
            <label className="mb-1.5 block text-[11.5px] font-bold tracking-wide" style={{ color: SLATE }}>
              {t('remindDoctor.emailLabel', "Doctor's email")}
            </label>
            <div className="relative mb-3">
              <Mail size={16} style={{ color: SLATE }} className="absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                type="email"
                inputMode="email"
                autoCapitalize="none"
                autoCorrect="off"
                value={email}
                onChange={(e) => { setEmail(e.target.value); if (status === 'error') setStatus('input') }}
                placeholder="doctor@practice.com"
                disabled={status === 'sending'}
                className="w-full rounded-xl border-[1.5px] bg-white py-3 pl-9 pr-3 text-[14px] outline-none"
                style={{ borderColor: status === 'error' ? '#FCA5A5' : '#E2E8F0', color: '#1E293B' }}
              />
            </div>

            {/* Fax — coming soon */}
            <label className="mb-1.5 block text-[11.5px] font-bold tracking-wide" style={{ color: SLATE, opacity: 0.6 }}>
              {t('remindDoctor.faxLabel', "Doctor's fax")}
            </label>
            <div className="relative mb-3" style={{ opacity: 0.6 }}>
              <input
                type="text"
                disabled
                placeholder="(555) 123-4567"
                className="w-full rounded-xl border-[1.5px] py-3 px-3 text-[14px]"
                style={{ borderColor: '#E2E8F0', background: '#F4F6F9', color: '#94A3B8' }}
              />
              <span
                className="absolute right-2.5 top-1/2 -translate-y-1/2 rounded-full px-2 py-1 text-[10px] font-bold"
                style={{ color: SLATE, background: '#E7EDF3' }}
              >
                {t('remindDoctor.comingSoon', 'Coming soon')}
              </span>
            </div>

            {status === 'error' && (
              <p className="mb-2 text-[12px]" style={{ color: '#b91c1c' }}>{error}</p>
            )}

            <button
              onClick={send}
              disabled={!valid || status === 'sending'}
              className="mt-1 flex w-full items-center justify-center gap-2 rounded-xl py-3 text-[14.5px] font-bold text-white transition-all"
              style={{
                background: valid && status !== 'sending' ? BLUE : '#9cc8f2',
                boxShadow: valid && status !== 'sending' ? '0 4px 12px rgba(70,168,249,0.3)' : 'none',
              }}
            >
              {status === 'sending'
                ? (<><OrellaSpinner size={17} tone="inherit" /> {t('remindDoctor.sending', 'Sending…')}</>)
                : (<><Send size={17} /> {t('remindDoctor.send', 'Send reminder')}</>)}
            </button>
            <p className="mt-3 text-center text-[10.7px] leading-relaxed" style={{ color: SLATE }}>
              {t('remindDoctor.foot', "Orella sends this on your behalf. We'll note that you invited them, and include an easy way to opt out.")}
            </p>
          </>
        )}
      </div>
    </div>
    </OverlayPortal>
  )
}
